import React, { useMemo, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import api from 'api';
import util from 'utils/utils';
import { Button } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import actions from 'actions';
import toast from 'react-hot-toast';
import useThrottle from 'utils/useThrottle';

import TagModalChooser from 'components/lib/Choosers/Tags/TagModalChooser';

const IdeaTags = ({
  user, challenge, updateIdea, idea, updateTagList, isSubmission = false,
}) => {
  const currentChallenge = challenge || idea.ownerChallenge;
  const org = currentChallenge?.organisation;
  const canEdit = useMemo(() => util.canEditIdea(user, idea) || util.canManageChallenge(user, currentChallenge), [user, idea, currentChallenge]);
  const canEditTagData = useMemo(() => util.canEditIdea(user, idea), [user, idea]);
  const tagsRelatedData = useMemo(() => idea.tagsRelatedData || [], [idea]);

  const updateTagListWithTag = useCallback((tag) => {
    const tagList = [...currentChallenge.tagList];
    const index = tagList.findIndex((t) => t._id === tag._id);
    tagList[index] = tag;

    if (challenge) {
      updateTagList(challenge._id, tagList);
    } else {
      updateIdea({ ...currentChallenge, tagList }, ['ownerChallenge'], true);
    }
  }, [updateTagList, updateIdea, currentChallenge, challenge]);

  const handleSavingUpdatedTagsRelatedData = useThrottle((tagId, value, prevTagsData) => {
    api.tags.updateTaggedDocument(org, 'ideas', idea._id, tagId, { value }, () => { }, (err) => {
      toast.error(err.message);
      updateIdea(prevTagsData, ['tagsRelatedData'], true);
    });
  }, 1000, [org, idea._id, updateIdea]);

  const updateTagsRelatedData = useCallback((tagId, value) => {
    const prevTagsRelatedData = tagsRelatedData;
    const filteredTagsRelatedData = tagsRelatedData.filter((dataItem) => dataItem.tagId !== tagId);
    const updatedTagsRelatedData = [...filteredTagsRelatedData, { tagId, value }];
    updateIdea(updatedTagsRelatedData, ['tagsRelatedData'], true);
    handleSavingUpdatedTagsRelatedData(tagId, value, prevTagsRelatedData);
  }, [tagsRelatedData, updateIdea, handleSavingUpdatedTagsRelatedData]);

  const addTag = useCallback((tag) => {
    api.tags.tagSingle(org, 'ideas', tag._id, idea._id, (updatedTag) => {
      const fullTags = [...idea.fullTags];
      fullTags.push(updatedTag);
      updateIdea(fullTags, ['fullTags'], true);
      updateIdea(fullTags.map((t) => t._id), ['tags'], true);
      updateTagListWithTag(updatedTag);
    }, (err) => toast.error(err.message));
  }, [org, idea, updateIdea, updateTagListWithTag]);

  const removeTag = useCallback((tag) => {
    api.tags.untagSingle(org, 'ideas', tag._id, idea._id, (updatedTag) => {
      const fullTags = [...idea.fullTags];
      const cIndex = fullTags.findIndex((t) => t._id === updatedTag._id);
      fullTags.splice(cIndex, 1);
      updateIdea(fullTags, ['fullTags'], true);
      updateIdea(fullTags.map((t) => t._id), ['tags'], true);
      updateTagListWithTag(updatedTag);
    });
  }, [org, idea, updateIdea, updateTagListWithTag]);

  if (!currentChallenge) {
    return null;
  }

  return (
    <>
      <TagModalChooser
        trigger={!isSubmission && <Button compact circular icon='hashtag' content='Edit tags' size='tiny' style={{ marginLeft: 3 }} />}
        showTags
        tags={idea.fullTags}
        availableTags={currentChallenge.tagList}
        addTag={addTag}
        removeTag={removeTag}
        canEdit={canEdit}
        tagsRelatedData={tagsRelatedData}
        onEditTagsRelatedData={(tagId, value) => updateTagsRelatedData(tagId, value)}
        canEditTagsRelatedData={canEditTagData}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const challenge = state.challenges.challenges.filter((c) => c._id === ownProps.idea.challenge)[0];
  return { user: state.user, challenge };
};

const mapDispatchToProps = (dispatch) => ({
  updateTagList: (id, tagList) => dispatch(actions.challenges.update(id, { tagList })),
});

const IdeaTagsContainer = withTranslation()(connect(
  mapStateToProps, mapDispatchToProps,
)(IdeaTags));

export default withTranslation()(withRouter(IdeaTagsContainer));
